/**
 * Analytics utility — R2 送信レイヤー
 *
 * ページアクセスから離脱までの全イベントを sessionStorage 上の UUID に紐づけ
 * メモリキューに溜め、pagehide / visibilitychange(hidden) で `/api/collect` に
 * `navigator.sendBeacon` で一括送信する（失敗時は fetch keepalive fallback）。
 *
 * ・Cookie / GA(gtag) 不使用 = 同意バナー不要
 * ・sendBeacon の 4KB 上限に備えて 3KB 手前で先行 flush
 * ・pagehide と visibilitychange(hidden) の二重発火に耐える（session_end は1回だけ）
 * ・sessionStorage 例外環境（プライベートブラウジング等）でもクラッシュしない
 *
 * 受信・R2書き込みは functions/api/collect.js 側で処理する。
 */

const ENDPOINT = '/api/collect';
const SESSION_KEY = 'sr_ar_session_id';
const FLUSH_THRESHOLD_BYTES = 3072;
// 入口フィルタ用の共有キー。bundle に埋め込まれる = View Source で見える前提。
// 目的は「/api/collect を誰かが手当たり次第 POST する」casual 攻撃の遮断。
// 本気の攻撃者対策は Cloudflare Rate Limit 側で行う。
//
// ⚠️ この値を変更したら、以下のファイルの同名定数も**必ず同じ値**に更新すること。
//    片方だけ変えると全 POST が 403 で拒否され、R2 に1件も届かなくなる（サイレント本番障害）。
//    - functions/api/collect.js の INGEST_KEY
//    ローテーション手順: 両方のリテラルを同時に書き換え → build → deploy を1トランザクションで実施。
const INGEST_KEY = 'sr-ar-v1-9x8k2m3p';

let sessionIdCache = null;
let queue = [];
let queueBytes = 0;
let flushSeq = 0; // 同一セッション内の flush 通番。R2 の書き込みキーに混ぜて上書き回避
let flushListenerAttached = false;
let sessionEndEnqueued = false;

export function getOrCreateSessionId() {
  if (sessionIdCache) return sessionIdCache;

  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      sessionIdCache = existing;
      return sessionIdCache;
    }
  } catch {
    // storage disabled — 発行フローに進む
  }

  sessionIdCache =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  try {
    sessionStorage.setItem(SESSION_KEY, sessionIdCache);
  } catch {
    // private browsing 等 — メモリキャッシュだけで続行
  }

  return sessionIdCache;
}

export function enqueueEvent(evt, payload = {}) {
  const record = {
    ts: Date.now(),
    session: getOrCreateSessionId(),
    evt,
    ...payload,
  };
  queue.push(record);
  queueBytes += JSON.stringify(record).length + 1; // +1 = ','

  if (queueBytes > FLUSH_THRESHOLD_BYTES) {
    flushToR2();
  }
}

export function flushToR2() {
  if (queue.length === 0) return;

  // seq は flush ごとに単調増加。サーバー側で session-<uuid>-<seq>.jsonl として
  // 別ファイルに書き出させるため、2回目以降の flush が1回目を上書きしない。
  const body = JSON.stringify({ k: INGEST_KEY, seq: flushSeq, events: queue });
  flushSeq += 1;
  queue = [];
  queueBytes = 0;

  let sent = false;
  try {
    sent = navigator.sendBeacon(ENDPOINT, body);
  } catch {
    sent = false;
  }

  if (!sent) {
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body,
      }).catch(() => {});
    } catch {
      // 送信不可 — サイレントに諦める（AR体験は止めない）
    }
  }
}

/**
 * ページ離脱系イベント (pagehide / visibilitychange hidden) に対して:
 *  - 初回発火時のみ session_end を積む（複数回タブ切替しても1回だけ）
 *  - 毎回 flush する（初回以降は差分イベントを追送）
 * リスナー登録は1回だけ (idempotent)。
 */
export function attachPagehideFlush() {
  if (flushListenerAttached) return;
  flushListenerAttached = true;

  const finalize = () => {
    if (!sessionEndEnqueued) {
      sessionEndEnqueued = true;
      trackSessionEnd();
    }
    flushToR2();
  };

  window.addEventListener('pagehide', finalize);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') finalize();
  });
}

// ---------- 既存 6 関数 (API維持) ----------

export function trackPageView(pagePath, pageTitle) {
  enqueueEvent('page_view', { page_path: pagePath, page_title: pageTitle });
}

export function trackARSessionStart(posterId, deliveryType = 'unknown') {
  enqueueEvent('ar_session_start', { poster_id: posterId, delivery_type: deliveryType });
}

export function trackVideoLoad(videoId, loadTime, deliveryType = 'unknown') {
  enqueueEvent('video_load', {
    video_id: videoId,
    load_time_ms: Math.round(loadTime),
    delivery_type: deliveryType,
  });
}

export function trackMarkerDetection(markerId, detectionTime) {
  enqueueEvent('marker_detected', {
    marker_id: markerId,
    detection_time_ms: Math.round(detectionTime),
  });
}

export function trackError(errorType, errorMessage) {
  enqueueEvent('error', { error_type: errorType, error_message: errorMessage });
}

export function trackNetworkSpeed(networkSpeed) {
  enqueueEvent('network_speed', { network_speed: networkSpeed });
}

// ---------- 新規 6 関数 ----------

export function trackCameraGrant(granted) {
  enqueueEvent('camera_grant', { granted: !!granted });
}

export function trackMarkerEnd(markerId, durationMs) {
  enqueueEvent('marker_end', { marker_id: markerId, duration_ms: Math.round(durationMs) });
}

export function trackVideoProgress(videoId, pct) {
  enqueueEvent('video_progress', { video_id: videoId, pct });
}

export function trackVideoEnded(videoId) {
  enqueueEvent('video_ended', { video_id: videoId });
}

export function trackCTAClick(ctaUrl) {
  enqueueEvent('cta_click', { cta_url: ctaUrl });
}

export function trackSessionEnd() {
  enqueueEvent('session_end');
}

// ---------- テスト用ヘルパー ----------

export function __resetForTests() {
  sessionIdCache = null;
  queue = [];
  queueBytes = 0;
  flushSeq = 0;
  flushListenerAttached = false;
  sessionEndEnqueued = false;
}
